import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, Copy, Loader2, RotateCcw, Send, Square, X } from '../../../icons';
import { tWorkspace } from '../../../lib/workspace-i18n';
import { cancelOfficeAiTurn, startOfficeAiTurn } from '../../../api/office-ai';
import { runOfficeAiAgent, type AgentMessage, type HostAgentTool } from '../../../api/office-ai-agent';
import { createTextTools, createRenameTool } from './text-tools';
import { useEnterToSend } from '../../../hooks/useEnterToSend';

/**
 * **호스트 폴백 AI 카드** — 한글(HWP)·평문 편집기용 (2026-08-15, PR3).
 *
 * ## 왜 이것만 따로 만드는가
 *
 * GenOffice 다섯(md·Word·PDF·Excel·PPT)은 챗박스를 **번들 안에 이미 갖고 있어서** PR2 는
 * 그것을 리스킨해 띄웠다. 한글에는 상류 코파일럿이 아예 없고(rhwp grep 0건), 평문은
 * 편집기 자체가 우리 `<textarea>` 다. 그래서 이 둘만 카드를 호스트가 그린다.
 *
 * 갈리는 것은 카드뿐이다 — 여는 버튼(FAB)은 일곱 편집기가 **문자 그대로 같은 컴포넌트**를
 * 쓴다(`WorkspaceDocAi`). 기하도 같은 `--wsai-*` 변수를 읽어 두 변종이 같은 자리에 뜬다.
 *
 * ## 도구가 없다 (강등 모드)
 *
 * 서버는 같은 엔드포인트를 `tools: []` 로 받으면 도구 구획 대신 "직접 고치지 말고 제안하라"
 * 지시를 넣는다(`office-ai-encode.ts` 의 `NO_TOOLS_NOTE`). 즉 이 카드는 **문서를 고치지
 * 않는다.** 그 사실을 빈 화면에서 먼저 말하고(`no_edit_note`), 답변마다 사용자가 스스로
 * 적용할 손잡이(복사 / 평문은 편집기에 삽입)를 준다.
 *
 * ## 항상 마운트한다
 *
 * 닫힘을 언마운트로 표현하지 않는다 — 그러면 대화 로그가 날아간다(상류 docs·slides 가
 * 그렇게 만들어져 있어 PR2 가 겪은 문제다). 표시만 CSS 로 가르므로 슬라이드 전이도
 * 첫 프레임 문제 없이 성립한다(`MemoPanel` 의 강제 리플로우가 필요 없는 이유).
 */

/** 이 카드가 문서에 닿는 창구 — 편집기 종류를 호스트가 하나로 좁혀 넘긴다. */
export interface WorkspaceDocAiTarget {
  /** 서버 감사·프롬프트 문구가 쓰는 편집기 종류. */
  editorKind: 'hwp' | 'text';
  fileId: string;
  fileName: string;
  /** 문서 본문. 못 읽으면 null — 카드는 그때도 돌고 "일반 지식으로만 답한다" 고 알린다. */
  getText: () => string | null;
  /** 있으면 "편집기에 삽입" 이 뜬다. 한글은 아직 없다(계획서 §8, PR5 의 몫). */
  insertText?: (text: string) => boolean;
  /**
   * 있으면 **AI 가 문서를 직접 고친다** (2026-08-15, Phase B).
   *
   * 이 하나로 카드의 모드가 갈린다 — 있으면 도구를 실어 보내고 호스트 루프를 돌리고,
   * 없으면 종전대로 `tools: []` 강등(제안만)이다. 한글은 아직 없다(Phase C).
   */
  setText?: (text: string) => boolean;
  /**
   * 있으면 AI 가 **파일 이름도 바꾼다** (2026-08-15).
   *
   * 없을 때 목록에서 도구가 통째로 빠지므로, 모델이 "이름도 바꿔 드릴까요?" 하고는
   * 못 하는 앞뒤 안 맞는 대화가 생기지 않는다.
   *
   * ★ 이것 하나만 있어도 카드는 **도구 모드**로 돈다(문서 편집은 못 해도).
   */
  renameFile?: (name: string) => Promise<string>;
  /**
   * 있으면 **편집기가 준 도구**로 문서를 고친다 (2026-08-15, Phase C — 한글).
   *
   * 평문처럼 글 전체를 갈아끼울 수 없는 편집기가 쓰는 길이다. 카드는 도구의 속을 모르고
   * 루프에 실어 보내기만 한다 — 문단·서식·개체를 아는 것은 편집기뿐이다.
   */
  getTools?: () => HostAgentTool[];
  /**
   * 편집기가 자기 되돌리기 스택을 쥔 경우의 되돌리기 (`getTools` 와 짝).
   *
   * ⚠ 없으면 카드는 되돌리기 버튼을 **안 그린다** — 눌러도 아무 일 없는 버튼이 가장 나쁘다.
   */
  undo?: (steps: number) => boolean;
}

/**
 * 도구 모드인가 — **무언가를 실제로 할 수 있는 창구가 하나라도 있는가**.
 *
 * 없으면 강등 모드로 남는다(서버가 `tools: []` 를 보고 "고치지 말고 제안하라" 를 넣는다).
 * 읽기 도구만 실어 보내는 상태는 만들지 않는다 — 문서 본문은 이미 사용자 메시지에 붙어
 * 가므로 얻는 것 없이 "도구가 있다" 는 신호만 주게 된다.
 */
function canAct(target: WorkspaceDocAiTarget): boolean {
  return !!target.setText || !!target.renameFile || !!target.getTools;
}

/** 본문을 직접 고칠 수 있는가 — 시스템 프롬프트가 이걸로 갈린다(이름만 바꾸는 것과 다르다). */
function canEditBody(target: WorkspaceDocAiTarget): boolean {
  return !!target.setText || !!target.getTools;
}

/**
 * 이 카드가 실을 도구.
 *
 * ⚠ 목록을 만드는 자리가 한 곳이어야 한다 — 모드 판정과 실제로 싣는 목록이 갈리면,
 *   도구가 있다고 프롬프트에 적어 놓고 빈 목록을 보내는 상태가 조용히 생긴다.
 */
function toolsFor(target: WorkspaceDocAiTarget): HostAgentTool[] {
  // 편집기가 자기 도구를 주면 그것이 문서 편집의 전부다(한글) — 평문용 줄 단위 도구는
  // 문단·서식이 있는 문서에 쓸 수 없다. 파일 이름만 공통으로 얹는다.
  const own = target.getTools?.();
  if (own?.length) {
    return target.renameFile
      ? [...own, createRenameTool(target.renameFile, () => target.fileName)]
      : own;
  }
  return createTextTools({
    getText: () => readNow(target),
    ...(target.setText ? { setText: target.setText } : {}),
    getFileName: () => target.fileName,
    ...(target.renameFile ? { renameFile: target.renameFile } : {}),
  });
}

/** 사람에게 보여 줄 도구 실행 한 줄. */
interface ToolActivity {
  name: string;
  summary: string;
  running?: boolean;
  isError?: boolean;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  /** 오류로 끝난 답변 — 다시 물어보라는 표시만 다르다. */
  failed?: boolean;
  /** 이 답변이 돌린 도구들. 없으면 카드를 안 그린다. */
  tools?: ToolActivity[];
  /** 문서를 고쳤다면 그 직전 내용 — 되돌리기 한 단계(평문: 전문을 되쓴다). */
  undoTo?: string;
  /**
   * 편집기 되돌리기를 몇 단계 풀어야 이 답변 이전인가 (한글).
   *
   * 한 도구 호출이 한 단계라 **문서를 바꾼 도구의 수**와 같다. 전문을 들고 있을 수 없는
   * 편집기(문단·서식·개체)에서 되돌리기를 성립시키는 유일한 방법이다.
   */
  undoSteps?: number;
}

/**
 * 문서 컨텍스트로 실어 보내는 글자 수 상한.
 *
 * 서버 예산은 110,000 **바이트**이고 한글은 3바이트/자다(`office-ai-encode.ts`). 대화 기록과
 * 지시문 몫을 빼고 잡은 값 — 넘치면 서버 사다리가 마지막 사용자 메시지의 컨텍스트를 다시
 * 자르지만(4,000자), 여기서 먼저 줄여야 2MB 본문을 헛되이 올려 보내지 않는다.
 */
const CONTEXT_MAX_CHARS = 24_000;

export function WorkspaceDocAiChat({ open, onClose, target }: {
  open: boolean;
  onClose: () => void;
  target: WorkspaceDocAiTarget;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  /** 문서 글을 못 읽었다 — 답변 신뢰도를 사용자가 알아야 한다. */
  const [noContext, setNoContext] = useState(false);
  const [trimmed, setTrimmed] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  /** 진행 중인 턴의 도구들 — 끝나면 답변 줄로 옮겨 붙는다. */
  const [liveTools, setLiveTools] = useState<ToolActivity[]>([]);
  /** 도구 모드의 중단 신호. 루프가 턴 경계마다 본다. */
  const abortRef = useRef<AbortController | null>(null);

  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** 진행 중인 턴의 requestId — 중지·정리에 쓴다. */
  const inflightRef = useRef<string | null>(null);
  /** 한 카드에서 이어지는 대화의 식별자(서버 감사·동시 실행 게이트가 본다). */
  const runId = useMemo(() => newId(), []);

  // 파일이 바뀌면 대화를 비운다 — 다른 문서 얘기가 남아 있으면 답이 그 문서로 샌다.
  useEffect(() => {
    setMessages([]);
    setInput('');
    setNoContext(false);
    setTrimmed(false);
  }, [target.fileId]);

  // 새 줄이 붙으면 로그를 아래로 — 사람이 매번 스크롤하게 두지 않는다.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  /**
   * 입력칸 자동 높이 — 다섯 패널의 `AiComposer` 와 같은 규격(한 줄에서 시작해 168px 까지).
   *
   * ⚠ `auto` 로 한 번 되돌린 뒤 재야 한다. 줄어들 때 `scrollHeight` 는 지금 높이를 넘지
   *   못해서, 안 그러면 한 번 늘어난 칸이 영영 안 줄어든다.
   */
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 168)}px`;
  }, [input]);

  // 열릴 때 입력에 포커스. 전이(220ms)가 끝난 뒤라야 스크롤이 튀지 않는다.
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => inputRef.current?.focus(), 240);
    return () => window.clearTimeout(id);
  }, [open]);

  // 카드를 걷어낼 때 진행 중인 턴을 반드시 끊는다 — 안 그러면 답이 없는 곳으로 흘러들고
  // 서버의 동시 실행 슬롯도 그만큼 오래 잡힌다(취소는 멱등이다).
  useEffect(() => () => {
    abortRef.current?.abort();
    if (inflightRef.current) cancelOfficeAiTurn(inflightRef.current);
  }, []);

  const stop = useCallback(() => {
    // 도구 모드는 루프가 턴을 잇는다 — 지금 턴만 끊으면 다음 턴이 또 나간다.
    abortRef.current?.abort();
    const id = inflightRef.current;
    if (!id) return;
    cancelOfficeAiTurn(id);
  }, []);

  /**
   * 도구 모드 — 편집기가 창구(`setText` 또는 `renameFile`)를 하나라도 준 경우.
   *
   * 강등 모드와 갈리는 곳은 여기 하나다. 서버 계약도 엔드포인트도 같고, 다른 것은
   * **도구를 실어 보내고 호스트 루프가 턴을 잇는다**는 점뿐이다.
   */
  const runWithTools = useCallback(async (
    history: ChatMessage[],
    doc: { text: string | null },
  ): Promise<void> => {
    if (!canAct(target)) return;

    const before = readNow(target);
    const ac = new AbortController();
    abortRef.current = ac;

    const activities: ToolActivity[] = [];
    const indexById = new Map<string, number>();
    const publish = (): void => setLiveTools([...activities]);
    const upsert = (id: string, next: ToolActivity): void => {
      const at = indexById.get(id);
      if (at === undefined) {
        indexById.set(id, activities.length);
        activities.push(next);
      } else {
        activities[at] = next;
      }
      publish();
    };

    // ⚠ 문서 본문은 **마지막 사용자 메시지에만** 싣는다(강등 모드와 같은 이유).
    //   도구 모드에서는 모델이 `read_text` 로 최신 내용을 다시 읽으므로 첫 컨텍스트는
    //   "무엇을 고칠지 고르는" 용도다.
    const payload: AgentMessage[] = history.map((m, i) => (
      i === history.length - 1 && doc.text
        ? { role: m.role, text: `${m.text}\n\n[문서: ${target.fileName}]\n${doc.text}` }
        : { role: m.role, text: m.text }
    ));

    let text = '';
    let failed: string | undefined;
    /** 문서를 실제로 바꾼 도구의 수 = 편집기 되돌리기 단계 수. */
    let edits = 0;
    try {
      const result = await runOfficeAiAgent({
        runId,
        editorKind: target.editorKind,
        fileId: target.fileId,
        system: buildSystem(target, !!doc.text, true),
        messages: payload,
        tools: toolsFor(target),
        signal: ac.signal,
        onToolStart: (call) => upsert(call.id, { name: call.name, summary: labelOf(call.name), running: true }),
        onToolResult: (call, res) => {
          // 편집기가 되돌리기를 쥔 경우, 몇 단계를 풀어야 하는지는 **바꾼 도구의 수**다.
          if (res.mutated && !res.isError) edits += 1;
          upsert(call.id, {
            name: call.name,
            summary: res.summary ?? labelOf(call.name),
            ...(res.isError ? { isError: true } : {}),
          });
        },
      });
      text = result.text;
      if (result.stop === 'error') failed = result.error;
      // max-turns 는 실패가 아니다 — 한 만큼은 남기고 더 시킬지는 사용자가 정한다.
    } catch (err) {
      failed = (err as Error)?.message || tWorkspace('workspace.ai.failed');
    }

    abortRef.current = null;
    setBusy(false);
    setLiveTools([]);

    const after = readNow(target);
    const mutated = edits > 0 || (after !== null && before !== null && after !== before);
    const body = text.trim() || (mutated ? tWorkspace('workspace.ai.edited') : '');
    if (!body && !failed && !activities.length) return; // 취소 등 — 빈 줄을 만들지 않는다

    setMessages((prev) => [...prev, {
      role: 'assistant',
      text: failed ? (body || failed) : body,
      ...(failed ? { failed: true } : {}),
      ...(activities.length ? { tools: activities } : {}),
      // 되돌리기는 두 갈래다: 평문은 이전 전문을 되쓰고(undoTo), 편집기가 자기 스택을
      // 쥔 쪽은 단계 수를 센다(undoSteps). 둘 다 없으면 버튼을 안 그린다.
      ...(target.undo && edits > 0
        ? { undoSteps: edits }
        : mutated && before !== null && target.setText ? { undoTo: before } : {}),
    }]);
  }, [runId, target]);

  const send = useCallback(() => {
    const question = input.trim();
    if (!question || busy) return;

    const history = [...messages, { role: 'user' as const, text: question }];
    setMessages(history);
    setInput('');
    setBusy(true);

    // ⚠ 문서 컨텍스트는 **마지막 사용자 메시지에만** 싣는다. 서버 예산 사다리가 마지막
    //   사용자 메시지의 `\n\n` 뒤를 컨텍스트로 보고 자르기 때문이고(office-ai-encode.ts),
    //   턴마다 전문을 다시 실으면 대화가 길어질수록 예산이 문서로만 채워진다.
    const doc = safeRead(target);
    setNoContext(!doc.text);
    setTrimmed(doc.trimmed);

    // 편집기가 창구를 하나라도 줬으면 도구 모드로 간다. 아니면 아래 강등 경로(회귀 없음).
    if (canAct(target)) {
      void runWithTools(history, doc);
      return;
    }

    const payloadMessages = history.map((m, i) => (
      i === history.length - 1 && doc.text
        ? { role: m.role, text: `${m.text}\n\n[문서: ${target.fileName}]\n${doc.text}` }
        : { role: m.role, text: m.text }
    ));

    const requestId = newId();
    inflightRef.current = requestId;
    let answer = '';
    let settled = false;

    const finish = (text: string, failed?: boolean): void => {
      if (settled) return;
      settled = true;
      inflightRef.current = null;
      setBusy(false);
      const body = text.trim();
      if (!body && !failed) return; // 빈 답은 줄을 만들지 않는다(취소한 경우 등).
      setMessages((prev) => [...prev, { role: 'assistant', text: body || tWorkspace('workspace.ai.failed'), failed }]);
    };

    void startOfficeAiTurn(
      {
        requestId,
        runId,
        editorKind: target.editorKind,
        fileId: target.fileId,
        system: buildSystem(target, !!doc.text),
        messages: payloadMessages,
        // ⚠ 빈 배열이 곧 강등 모드다 — 서버가 도구 구획 대신 "제안만" 지시를 넣는다.
        tools: [],
      },
      {
        onPing: () => {},
        onDelta: (text) => { answer += text; },
        // 도구를 안 줬으므로 오지 않아야 한다. 그래도 오면 조용히 버린다 —
        // 실행할 도구가 없는데 사용자에게 보여 봐야 읽을 수 없는 JSON 일 뿐이다.
        onToolCall: () => {},
        onDone: () => finish(answer),
        onError: (message) => finish(message || tWorkspace('workspace.ai.failed'), true),
      },
    ).catch((err: unknown) => {
      finish((err as Error)?.message || tWorkspace('workspace.ai.failed'), true);
    });
  }, [busy, input, messages, runId, runWithTools, target]);

  /**
   * Enter 로 보내기 — **한글 조합 중에도 한 번에**.
   *
   * 조합을 확정하는 Enter 는 keydown 에서 `isComposing` 이 참이라 종전 조건이 걸러 냈고,
   * 그래서 한글 문장 끝에서는 Enter 를 **두 번** 눌러야 했다. 훅이 그 Enter 를 기억해
   * keyup 에서 갚는다.
   */
  const enterToSend = useEnterToSend<HTMLTextAreaElement>(send);

  const note = (text: string): void => {
    setFlash(text);
    window.setTimeout(() => setFlash((cur) => (cur === text ? null : cur)), 1600);
  };

  const copy = (text: string): void => {
    void writeClipboard(text).then((ok) => { if (ok) note(tWorkspace('workspace.ai.copied')); });
  };

  const insert = (text: string): void => {
    if (target.insertText?.(text)) note(tWorkspace('workspace.ai.inserted'));
  };

  const undo = (m: ChatMessage): void => {
    // 편집기가 되돌리기를 쥔 쪽(한글)은 단계 수만큼 풀고, 평문은 이전 전문을 되쓴다.
    const ok = m.undoSteps !== undefined
      ? target.undo?.(m.undoSteps) === true
      : m.undoTo !== undefined && target.setText?.(m.undoTo) === true;
    if (ok) note(tWorkspace('workspace.ai.undone'));
  };

  const title = tWorkspace('workspace.ai.title');

  return (
    <section
      className={`wsai-card${open ? ' wsai-card--open' : ''}`}
      role="dialog"
      aria-label={title}
      aria-hidden={!open}
    >
      <header className="wsai-card__header">
        <span className="wsai-card__title">{title}</span>
        <button
          type="button"
          className="wsai-card__icon"
          onClick={onClose}
          aria-label={tWorkspace('workspace.ai.close')}
        >
          <X size={16} aria-hidden />
        </button>
      </header>

      <div className="wsai-card__log" ref={logRef} role="log" aria-live="polite">
        {/*
          빈 상태 — GenOffice 다섯과 **같은 규격**(제목 + 설명, 가운데). 대화가 시작되면
          사라진다: 안내를 로그 맨 위에 계속 두면 스크롤을 거슬러 올라갔을 때 지난 대화보다
          안내가 먼저 나와 대화의 순서가 어긋난다.
        */}
        {messages.length === 0 && (
          <div className="wsai-empty">
            <div className="wsai-empty__title">{tWorkspace('workspace.ai.empty_title')}</div>
            <div className="wsai-empty__body">
              {/* 세 갈래다 — 본문까지 고친다 / 본문은 못 고치지만 도구는 있다 / 제안만. */}
              {tWorkspace(
                canEditBody(target)
                  ? 'workspace.ai.edit_note'
                  : canAct(target) ? 'workspace.ai.no_edit_note_tools' : 'workspace.ai.no_edit_note',
              )}
            </div>
          </div>
        )}
        {noContext && <p className="wsai-card__note wsai-card__note--warn">{tWorkspace('workspace.ai.no_context')}</p>}
        {trimmed && <p className="wsai-card__note">{tWorkspace('workspace.ai.context_trimmed')}</p>}

        {messages.map((m, i) => (
          <article
            key={i}
            className={`wsai-msg wsai-msg--${m.role}${m.failed ? ' wsai-msg--failed' : ''}`}
          >
            {/*
              ★ 말한 사람 이름표를 붙이지 않는다 — 다섯 패널의 규격이 그렇다(사용자는 우측
              알약, 어시스턴트는 평문). 좁은 폭에서 이름표는 한 줄을 통째로 먹는다.
              읽는 사람에게는 **자리와 모양**이 이미 누구인지 말한다.
            */}
            {m.tools && m.tools.length > 0 && <ToolSteps tools={m.tools} />}
            <div className="wsai-msg__body">{m.text}</div>
            {m.role === 'assistant' && !m.failed && (
              <div className="wsai-msg__actions">
                {/* 되돌리기가 먼저다 — 문서가 바뀐 직후 사람이 가장 먼저 찾는 손잡이다. */}
                {(m.undoTo !== undefined || m.undoSteps !== undefined) && (
                  <button type="button" className="wsai-msg__act" onClick={() => undo(m)}>
                    <RotateCcw size={12} aria-hidden /> {tWorkspace('workspace.ai.undo')}
                  </button>
                )}
                <button type="button" className="wsai-msg__act" onClick={() => copy(m.text)}>
                  <Copy size={12} aria-hidden /> {tWorkspace('workspace.ai.copy')}
                </button>
                {/* 삽입은 넣을 창구가 있을 때만 — 한글은 아직 없다(복사로 옮긴다). */}
                {target.insertText && (
                  <button type="button" className="wsai-msg__act" onClick={() => insert(m.text)}>
                    {tWorkspace('workspace.ai.insert')}
                  </button>
                )}
              </div>
            )}
          </article>
        ))}

        {/* 도는 중인 도구를 답변보다 먼저 보여 준다 — 수십 초짜리 턴에서 화면이 죽어 보이지 않게. */}
        {busy && liveTools.length > 0 && <ToolSteps tools={liveTools} />}

        {busy && (
          <p className="wsai-card__busy" role="status">
            <Loader2 size={13} className="wsai-spin" aria-hidden />{' '}
            {tWorkspace(liveTools.length ? 'workspace.ai.working' : 'workspace.ai.thinking')}
          </p>
        )}
      </div>

      {flash && <p className="wsai-card__flash" role="status">{flash}</p>}

      {/*
        컴포저 — 다섯 패널의 `AiComposer` 와 **같은 규격**이다(입력 상자 + 아래 줄에 힌트·전송).
        구조가 세 겹인 이유가 있다: 입력 상자가 카드다(테두리·그림자), 힌트와 전송은 그 상자
        **안쪽 아래**에 놓인다. 버튼을 상자 옆에 두면 좁은 폭(280px)에서 입력칸이 그만큼 좁아진다.
      */}
      <form
        className="wsai-card__composer"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        <div className="wsai-composer__box">
          <textarea
            ref={inputRef}
            className="wsai-card__input"
            rows={1}
            value={input}
            placeholder={tWorkspace('workspace.ai.placeholder')}
            aria-label={tWorkspace('workspace.ai.placeholder')}
            onChange={(e) => setInput(e.target.value)}
            // Enter 로 보내고 Shift+Enter 로 줄바꿈 — 채팅 입력의 집안 관례와 같다.
            // ⚠ 한글 조합 중 Enter 는 keydown 에서 걸러지므로(조합 확정 키) keyup 에서 갚는다.
            //   그 처리를 `useEnterToSend` 가 한다 — 안 그러면 Enter 를 두 번 눌러야 한다.
            {...enterToSend}
          />
          <div className="wsai-composer__footer">
            <span className="wsai-composer__hint">
              {tWorkspace(busy ? 'workspace.ai.hint_busy' : 'workspace.ai.hint_send')}
            </span>
            {busy ? (
              <button type="button" className="wsai-card__send" onClick={stop} aria-label={tWorkspace('workspace.ai.stop')}>
                <Square size={16} aria-hidden />
              </button>
            ) : (
              <button
                type="submit"
                className="wsai-card__send"
                disabled={!input.trim()}
                aria-label={tWorkspace('workspace.ai.send')}
              >
                <Send size={16} aria-hidden />
              </button>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}

/** 도구 실행 단계 — 상류 다섯 편집기의 `ai-work-group` 과 같은 정보 구성이다. */
function ToolSteps({ tools }: { tools: ToolActivity[] }) {
  const running = tools.some((t) => t.running);
  return (
    <div className="wsai-steps" role="group" aria-label={tWorkspace('workspace.ai.working')}>
      <span className="wsai-steps__label">
        {running
          ? tWorkspace('workspace.ai.working')
          : tWorkspace('workspace.ai.steps_done', { n: tools.length })}
      </span>
      {tools.map((t, i) => (
        <span
          key={i}
          className={`wsai-step${t.isError ? ' wsai-step--error' : ''}`}
          title={t.name}
        >
          <span className="wsai-step__icon" aria-hidden>
            {t.running
              ? <Loader2 size={11} className="wsai-spin" />
              : t.isError ? <AlertCircle size={11} /> : <Check size={11} />}
          </span>
          <span className="wsai-step__text">{t.summary}</span>
        </span>
      ))}
    </div>
  );
}

/** 편집기 이름 → 사람이 읽을 한 줄(요약이 없을 때의 기본값). */
function labelOf(name: string): string {
  return name.replace(/[_-]+/g, ' ');
}

/** 지금 화면의 글. 편집기 내부를 동기로 훑으므로 예외를 삼킨다. */
function readNow(target: WorkspaceDocAiTarget): string | null {
  try {
    return target.getText();
  } catch {
    return null;
  }
}

/**
 * 시스템 프롬프트.
 *
 * 도구 규약(“직접 고치지 말 것”)은 **서버가 붙인다** — 여기서 다시 적으면 두 지시가 갈릴 때
 * 어느 쪽이 이기는지 알 수 없다. 여기 적는 것은 서버가 모르는 것뿐이다: 지금 열린 문서가
 * 무엇이고, 본문을 함께 보냈는지.
 */
function buildSystem(target: WorkspaceDocAiTarget, hasDoc: boolean, tooled = false): string {
  const kind = target.editorKind === 'hwp' ? '한글(HWP/HWPX) 문서' : '평문 텍스트 파일';
  /**
   * 도구가 있다고 **본문**을 고칠 수 있는 것은 아니다 (2026-08-15).
   *
   * 한글은 파일 이름 바꾸기만 쥐고 본문 쓰기 창구가 없다. 이때 편집 지시문을 그대로 주면
   * 모델이 있지도 않은 편집 도구를 부르다 세 번 실패하고 멈춘다.
   */
  const editsBody = tooled && canEditBody(target);
  /** 줄 단위(평문)냐 문단 단위(한글)냐 — 도구 이름과 축이 다르므로 지시문도 갈린다. */
  const readTool = target.getTools ? '`read_text` 로 현재 내용과 문단 번호' : '`read_text` 로 현재 내용과 줄 번호';
  return [
    `너는 사내 워크스페이스 문서 편집기에 붙은 AI 어시스턴트다. 사용자가 지금 열어 둔 것은 ${kind} "${target.fileName}" 이다.`,
    hasDoc
      ? '사용자 메시지 끝의 `[문서: …]` 구획이 그 문서의 본문이다. 문서에 없는 사실을 지어내지 말고, 근거가 없으면 없다고 말한다.'
      : '문서 본문은 함께 오지 않았다. 문서 내용을 아는 척하지 말고, 일반 지식으로 답하거나 무엇을 알려주면 되는지 되묻는다.',
    // ⚠ 도구 모드에서 "초안을 써 주라" 고 하면 모델이 도구 대신 산문으로 답한다.
    //   할 일이 갈리므로 두 지시를 섞지 않는다.
    editsBody
      ? [
          '너는 도구로 이 문서를 직접 고칠 수 있다. 고쳐 달라는 요청에는 초안을 쓰지 말고 도구를 불러 실제로 반영한다.',
          `고치기 전에 ${readTool}를 확인한다 — 함께 온 본문은 이미 낡았을 수 있다.`,
          '한 번에 한 곳씩 고치고, 끝나면 무엇을 어떻게 바꿨는지 한두 문장으로 요약한다.',
          '요청이 모호하면 고치지 말고 무엇을 바꿀지 먼저 되묻는다. 문서를 지우는 편집은 특히 확인 후에 한다.',
          // ⚠ 이 줄이 없으면 모델이 "이름도 바꿔 드릴까요?" 하고는 다음 턴에 "저는 못 합니다"
          //   라고 한다(2026-08-15 실제 대화). 할 수 있는 일은 도구 목록이 전부다.
          '네가 할 수 있는 일은 주어진 도구가 전부다. 도구에 없는 일은 해 주겠다고 제안하지 말고, 처음부터 할 수 없다고 말한다.',
        ].join('\n')
      : tooled
        ? [
            // 도구는 있지만 본문 쓰기는 없다(한글) — 할 수 있는 것과 없는 것을 분명히 가른다.
            '이 형식에서는 문서 **본문**을 직접 고칠 수 없다. 본문 수정 요청에는 그대로 붙여 넣을 수 있는 완결된 초안을 써 준다.',
            '다만 주어진 도구로 할 수 있는 일(파일 이름 바꾸기 등)은 초안이 아니라 도구를 불러 실제로 처리한다.',
            '네가 할 수 있는 일은 주어진 도구가 전부다. 도구에 없는 일은 해 주겠다고 제안하지 말고, 처음부터 할 수 없다고 말한다.',
          ].join('\n')
        : '초안을 요청받으면 그대로 붙여 넣을 수 있게 완결된 형태로 쓴다. 설명은 짧게, 초안이 주인공이다.',
    '답변은 사용자가 쓴 언어로 작성한다.',
  ].join('\n');
}

/** 문서 읽기는 편집기 내부를 동기로 훑는다 — 실패해도 카드는 계속 돌아야 한다. */
function safeRead(target: WorkspaceDocAiTarget): { text: string | null; trimmed: boolean } {
  let text: string | null = null;
  try {
    text = target.getText();
  } catch {
    text = null;
  }
  if (!text) return { text: null, trimmed: false };
  if (text.length <= CONTEXT_MAX_CHARS) return { text, trimmed: false };
  return { text: text.slice(0, CONTEXT_MAX_CHARS), trimmed: true };
}

/**
 * 클립보드 — 설치형(HTTP 오리진 포함)에서 `navigator.clipboard` 가 없을 수 있다.
 * 그때는 임시 textarea + `execCommand` 로 떨어진다(집안의 다른 복사 자리와 같은 처방).
 */
async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* 아래 폴백 */
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    el.remove();
    return ok;
  } catch {
    return false;
  }
}

/** requestId/runId — secure context 가 아니면 `crypto.randomUUID` 가 없다(집안 관례와 동일). */
function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `wsai-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
